import { useTranslation } from 'react-i18next';
import {
  useHouseholdActiveHolds,
  HouseholdHoldResponseTargetKind,
  type HouseholdHoldResponse,
} from '@kidshome/api-contract';
import { useFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { rewardGlyph } from '@/shared/ui';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { PauseCircleIcon } from '@/shared/ui/icons';
import { rewardArt, categoryArt } from '@/assets/icons';

/**
 * The household-wide "what's paused" strip — shown below the shell header for every guardian + relative
 * so a paused reward/category is impossible to miss (e.g. a parent puts candy on hold; grandma sees it
 * before granting a treat). It reads `GET /v1/economy/holds/active` (`economy:read`, every member has
 * it) and renders ONE glanceable chip per active hold: the targeted reward/category icon ({@link HoldIcon}
 * resolves a reward's icon-key slug to its art/glyph) + name (RAW user content, never translated —
 * ADR-0025) with the affected child muted beside it.
 *
 * Quiet by construction: nothing paused → renders nothing (no layout shift); a load error or pending is
 * silent (a banner is a glance, never a blocker). NOT shown to the kid persona — a child already sees a
 * held reward locked in their own shop, and surfacing "your candy is paused" household-wide would shame
 * rather than coordinate (kind gamification). Detail + lift live on `/admin/rewards` (ActiveHolds).
 */
export function PausedRewardsBanner(): React.ReactElement | null {
  const { t } = useTranslation('family');
  // Holds live in the Economy module — don't fetch (or show) when it's disabled (ADR-0076).
  const economyOn = useFeature()(Feature.Economy);
  const holds = useHouseholdActiveHolds({ enabled: economyOn });

  const items = economyOn ? (holds.data ?? []) : [];
  if (items.length === 0) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warning/40 bg-warning-fg px-4 py-2 text-sm lg:px-6"
    >
      <span className="flex items-center gap-1.5 font-semibold text-warning">
        <PauseCircleIcon size={16} className="shrink-0" />
        {t('pausedRewards.label')}
        {/* Count badge so a household with several holds reads the total at a glance, not just the chips. */}
        {items.length > 1 && (
          <span className="inline-flex min-w-5 items-center justify-center rounded-pill bg-warning px-1.5 text-xs font-bold tabular-nums text-warning-fg">
            {items.length}
          </span>
        )}
      </span>
      <ul className="m-0 flex list-none flex-wrap items-center gap-2 p-0">
        {items.map((hold) => (
          <li
            key={hold.id}
            className="flex items-center gap-1.5 rounded-pill border border-warning/40 bg-surface px-2.5 py-0.5"
          >
            <HoldIcon hold={hold} />
            <span className="font-semibold text-text">{hold.targetLabel}</span>
            <span className="text-text-muted">
              {t('pausedRewards.forChild', { name: hold.childName })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The hold's target glyph. A CATEGORY hold's `targetIcon` is already an emoji (the category's stored
 * icon), but a REWARD hold's `targetIcon` is the reward's icon-KEY slug (e.g. "outing") — resolve it
 * through the reward art/glyph maps (mirrors {@link ActiveHolds}) so the chip never prints a raw slug.
 */
function HoldIcon({ hold }: { hold: HouseholdHoldResponse }): React.ReactElement | null {
  if (hold.targetKind === HouseholdHoldResponseTargetKind.reward) {
    const art = rewardArt(hold.targetIcon);
    return art ? (
      <img src={art} alt="" className="size-5 object-contain" />
    ) : (
      <span aria-hidden="true" className="text-base leading-none">
        {rewardGlyph(hold.targetIcon)}
      </span>
    );
  }
  if (!hold.targetIcon) return null;
  return <ConceptIcon art={categoryArt(hold.targetIcon)} glyph={hold.targetIcon} size={20} />;
}
